import { useMemo } from 'react';
import type { HelpArticle } from './types';
import styles from './HelpCenter.module.css';

interface HelpArticleViewProps {
  article: HelpArticle;
  onArticleClick?: (articleId: string) => void;
}

/**
 * Simple markdown-to-HTML renderer for help articles
 */
function renderMarkdown(content: string): string {
  let html = content;

  // Headers
  html = html.replace(/^### (.+)$/gm, '<h3>$1</h3>');
  html = html.replace(/^## (.+)$/gm, '<h2>$1</h2>');
  html = html.replace(/^# (.+)$/gm, '<h1>$1</h1>');

  // Bold and italic
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/\*(.+?)\*/g, '<em>$1</em>');
  html = html.replace(/__(.+?)__/g, '<strong>$1</strong>');
  html = html.replace(/_(.+?)_/g, '<em>$1</em>');

  // Code blocks
  html = html.replace(/```(\w*)\n([\s\S]*?)```/g, '<pre><code class="language-$1">$2</code></pre>');
  html = html.replace(/`(.+?)`/g, '<code>$1</code>');

  // Links
  html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');

  // Lists
  html = html.replace(/^\- (.+)$/gm, '<li>$1</li>');
  html = html.replace(/^\* (.+)$/gm, '<li>$1</li>');
  html = html.replace(/^\d+\. (.+)$/gm, '<li>$1</li>');
  html = html.replace(/(<li>.*<\/li>\n?)+/g, '<ul>$&</ul>');

  // Blockquotes
  html = html.replace(/^> (.+)$/gm, '<blockquote>$1</blockquote>');

  // Paragraphs
  html = html.replace(/\n\n/g, '</p><p>');
  html = '<p>' + html + '</p>';

  // Clean up empty paragraphs
  html = html.replace(/<p>\s*<\/p>/g, '');
  html = html.replace(/<p>(<h[1-3]>)/g, '$1');
  html = html.replace(/(<\/h[1-3]>)<\/p>/g, '$1');
  html = html.replace(/<p>(<ul>)/g, '$1');
  html = html.replace(/(<\/ul>)<\/p>/g, '$1');
  html = html.replace(/<p>(<pre>)/g, '$1');
  html = html.replace(/(<\/pre>)<\/p>/g, '$1');
  html = html.replace(/<p>(<blockquote>)/g, '$1');
  html = html.replace(/(<\/blockquote>)<\/p>/g, '$1');

  return html;
}

export function HelpArticleView({ article, onArticleClick }: HelpArticleViewProps) {
  const htmlContent = useMemo(() => renderMarkdown(article.content), [article.content]);

  return (
    <div className={styles.articleView}>
      {/* Article summary */}
      <div className={styles.articleSummary}>
        {article.summary}
      </div>

      {/* Article content */}
      <div
        className={styles.articleContent}
        dangerouslySetInnerHTML={{ __html: htmlContent }}
      />

      {/* Related articles */}
      {article.relatedArticles && article.relatedArticles.length > 0 && (
        <div className={styles.relatedArticles}>
          <h3 className={styles.relatedTitle}>Related Articles</h3>
          <div className={styles.relatedList}>
            {article.relatedArticles.map(articleId => (
              <button
                key={articleId}
                className={styles.relatedItem}
                onClick={() => onArticleClick?.(articleId)}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                </svg>
                <span>{articleId}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Keywords */}
      {article.keywords.length > 0 && (
        <div className={styles.articleKeywords}>
          <span className={styles.keywordsLabel}>Keywords:</span>
          {article.keywords.slice(0, 5).map(keyword => (
            <span key={keyword} className={styles.keyword}>
              {keyword}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
